import {MaterialOrderRestInterface} from './interfaces/_material-order.rest';
import {CTAPIConfiguration} from './utils/configuration';
import {RequestHelper} from './utils/request';
import {OrderFilter} from '../models/interfaces/_order-filter.model';
import {OrdersFiltered} from '../models/interfaces/_order-filtered.model';
import {OrderHelper} from '../models/helpers/order.helper';

import {from, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {NMaterialOrder} from '../models/interfaces/_material-order.model';

export class MaterialOrderRest implements MaterialOrderRestInterface {

    private configuration: CTAPIConfiguration;
    private requestHelper: RequestHelper;

    constructor(configuration: CTAPIConfiguration) {
        this.configuration = configuration;
        this.requestHelper = new RequestHelper(configuration);
    }

    /**
     * Request for filtered active and finished material orders.
     */
    filterMaterialOrders(filter: OrderFilter): Observable<OrdersFiltered> {
        return this.requestHelper.request({ data: filter }, this.configuration.materialOrder.filter,
            this.configuration.httpClient).pipe(
            map((response) => {
                // Related tickets: DEVSERVICE-47065, DEVSERVICE-47856
                // mapping sAddrUnit to sAddr, we know it's wrong. It was build like this before
                // ideally CT starts sending them separately in this API call so we can remove this
                // for now it's written like this as a fallback to unblock the customer
                // scenario : Create MO via copy order button.
                if (response.result && response.result.orders) {
                    response.result.orders = response.result.orders.map((order) => {
                        const origLoca = order.jOrigLoca;
                        if (origLoca && !origLoca.sAddr && !origLoca.sUnit && origLoca.sAddrUnit) {
                            order.jOrigLoca.sAddr = origLoca.sAddrUnit;
                        }
                        const destLoca = order.jDestLoca;
                        if (destLoca && !destLoca.sAddr && !destLoca.sUnit && destLoca.sAddrUnit) {
                            order.jDestLoca.sAddr = destLoca.sAddrUnit;
                        }
                        return order;
                    });
                }
                return response.result;
            })
        );
    }

    /**
     * Request for a material order by text code.
     */
    searchOrder(sSearchText: string, nDivID: number, environment: string, forceAnonymous: boolean = false): Observable<NMaterialOrder> {
        const materialOrderSearchEndpointConfig = forceAnonymous
            ? this.configuration.materialOrderAnonymous.search
            : this.configuration.materialOrder.search;

        return this.requestHelper.request({
                data: {
                    sSearchText,
                    nDivID,
                    sEnvCode: environment
                }
            }, materialOrderSearchEndpointConfig,
            this.configuration.httpClient).pipe(
            map((response) => {
                const order = response.result;
                const orderHelper: OrderHelper = new OrderHelper();

                // Someone gave up on modeling it and just cut out the parser
                // at some point we should fix this
                // return orderHelper.parseCTOrder(response.result);

                // added specific mapping here for some locations
                order.jRecommendedDepartPortLoca = orderHelper.normalizeLocation(order.jRecommendedDepartPortLoca);
                order.jRecommendedArrivePortLoca = orderHelper.normalizeLocation(order.jRecommendedArrivePortLoca);
                order.jShipperLoca = orderHelper.normalizeLocation(order.jShipperLoca);
                order.jConsigneeLoca = orderHelper.normalizeLocation(order.jConsigneeLoca);
                order.jNotifyLoca = orderHelper.normalizeLocation(order.jNotifyLoca);
                return order;
            })
        );
    }

    /**
     * Creation of Material Order.
     */
    createOrder(order) {
        return this.requestHelper.request({
                data:
                order
            }, this.configuration.materialOrder.create,
            this.configuration.httpClient).pipe(map((response) => {
            // const orderHelper: OrderHelper = new OrderHelper();
            // return orderHelper.parseCTOrder(response.result.order);
            return response.result;
        }));
    }

    /**
     * Request for order map data (ex: route).
     */
    getOrderMap(data, forceAnonymous: boolean = false) {
        const materialOrderMapInfoEndpointConfig = forceAnonymous
            ? this.configuration.materialOrderAnonymous.mapInfo
            : this.configuration.materialOrder.mapInfo;

        return from(this.requestHelper.request({data}, materialOrderMapInfoEndpointConfig,
            this.configuration.httpClient).pipe(map((response) => {
            return response.result;
        })));
    }

    /**
     * Request for order files.
     */
    getOrderRelatedFiles(orderId, nDivID, orderType, sEnvCode) {
        return from(this.requestHelper.request({
                data:
                    {
                        relObj:
                            {
                                type: orderType,
                                id: orderId
                            }, nDivID,
                        sEnvCode
                    }
            }, this.configuration.materialOrder.getRelatedFiles,
            this.configuration.httpClient).pipe(map((response) => {
            return response.result ? response.result.items : [];
        })));
    }
}
